import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IconButton, Grid, Typography, Alert } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const logoLabelSx = { 
  fontSize: '16px',
  fontWeight: '600',
  color: '#121A22',
  textAlign: 'left',
  fontFamily: 'Noto Sans',
  marginRight: '22px'
};

const normalizeProtocolValue = (value) => Array.from(value)
  .filter((char) => char.charCodeAt(0) > 0x20)
  .join('')
  .toLowerCase();

/**
 * Validates SVG content for malicious scripts and event handlers
 */
const validateSvgContent = (content) => {
  if (typeof content !== 'string') {
    return false;
  }

  try {
    const doc = new DOMParser().parseFromString(content, 'image/svg+xml');

    // Invalid XML/SVG commonly parses to a parsererror root element.
    if (doc.documentElement && doc.documentElement.nodeName.toLowerCase() === 'parsererror') {
      return false;
    }

    const root = doc.documentElement;
    if (!root || root.nodeName.toLowerCase() !== 'svg') {
      return false;
    }

    // Disallow active script-capable elements.
    if (doc.querySelector('script')) {
      return false;
    }

    const urlLikeAttributes = new Set(['href', 'xlink:href', 'src']);
    const cssDangerousUrlPattern = /url\s*\(\s*['"]?\s*(javascript:|data:)/i;

    for (const el of doc.querySelectorAll('*')) {
      for (const attr of Array.from(el.attributes)) {
        const attrName = attr.name.toLowerCase();
        const attrValue = String(attr.value || '').trim();

        // Block inline event handlers like onload, onclick, etc.
        if (attrName.startsWith('on')) {
          return false;
        }

        // Normalize control characters/whitespace to catch obfuscated protocols.
        const normalized = normalizeProtocolValue(attrValue);

        if (urlLikeAttributes.has(attrName)
          && (normalized.startsWith('javascript:') || normalized.startsWith('data:'))) {
          return false;
        }

        if (attrName === 'style' && cssDangerousUrlPattern.test(attrValue)) {
          return false;
        }
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Validates file type and content
 */
const validateFileBeforePreview = async (file, setError) => {
  if (!file) {
    setError('');
    return true;
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const isValidExtension = fileName.endsWith('.png') || fileName.endsWith('.svg');

  if (!isValidExtension) {
    setError('Only PNG or SVG files are allowed.');
    return false;
  }

  // Check file type
  const isValidType = file.type === 'image/png' || file.type === 'image/svg+xml' || file.type === 'image/svg';

  if (!isValidType) {
    setError('Only PNG or SVG files are allowed.');
    return false;
  }

  // Additional validation for SVG files
  if (fileName.endsWith('.svg') || file.type === 'image/svg+xml' || file.type === 'image/svg') {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target.result;
          if (!validateSvgContent(content)) {
            setError('Upload a valid SVG file');
            resolve(false);
            return;
          }

          setError('');
          resolve(true);
        } catch (error) {
          setError('Error validating SVG file');
          resolve(false);
        }
      };

      reader.onerror = () => {
        setError('Error validating SVG file');
        resolve(false);
      };

      reader.readAsText(file);
    });
  }

  setError('');
  return true;
};

const LogoUploader = () => {
  const { setValue, control, watch } = useFormContext();
  const watchLogo = watch('lightLogoFile', null);
  const watchDarkLogo = watch('darkLogoFile', null);
  const [lightLogoError, setLightLogoError] = useState('');
  const [darkLogoError, setDarkLogoError] = useState('');

  const deleteImage = () => {
    setValue('lightLogoFile', null);
    setLightLogoError('');
  };

  const deleteDarkLogo = () => {
    setValue('darkLogoFile', null);
    setDarkLogoError('');
  };

  return (
    <Grid container spacing={2}>
      {lightLogoError && (
        <Grid item xs={12}>
          <Alert severity="error">{lightLogoError}</Alert>
        </Grid>
      )}
      {watchLogo && (
        <>
          <Grid item xs={10}>
            <Grid container spacing={0} alignItems="center" justifyContent="center">
              <img
                alt="logo-preview"
                width="180"
                height="38"
                src={URL.createObjectURL(watchLogo)}
              />
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => deleteImage()} component="span" size="large">
              <HighlightOffIcon />
            </IconButton>
          </Grid>
        </>
      )}
      <Grid item xs={12} sx={{ marginBottom: '12px' }}>
        <Typography component="span" sx={logoLabelSx}>
          Logo 1
        </Typography>
        <Controller
          render={({ field }) => (
            <input
              type="file"
              name="input-image"
              aria-label="Logo 1"
              accept=".png, .svg"
              {...field}
              value={''}
              onChange={async (event) => {
                const file = event.target.files[0];
                const isValid = await validateFileBeforePreview(file, setLightLogoError);
                if (isValid) {
                  field.onChange(file);
                } else {
                  field.onChange(null);
                }
              }}
            />
          )}
          name="lightLogoFile"
          control={control}
        />
      </Grid>
      {darkLogoError && (
        <Grid item xs={12}>
          <Alert severity="error">{darkLogoError}</Alert>
        </Grid>
      )}
      {watchDarkLogo && (
        <>
          <Grid item xs={10}>
            <Grid container spacing={0} alignItems="center" justifyContent="center">
              <img
                alt="logo-preview"
                width="180"
                height="38"
                src={URL.createObjectURL(watchDarkLogo)}
                onLoad={(e) => URL.revokeObjectURL(e.currentTarget.src)}
                onError={(e) => URL.revokeObjectURL(e.currentTarget.src)}
              />
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => deleteDarkLogo()} component="span" size="large">
              <HighlightOffIcon />
            </IconButton>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Typography component="span" sx={logoLabelSx}>
          Logo 2
        </Typography>
        <Controller
          render={({ field }) => (
            <input
              type="file"
              name="input-image-2"
              aria-label="Logo 2"
              accept=".png, .svg"
              {...field}
              value={''}
              onChange={async (event) => {
                const file = event.target.files[0];
                const isValid = await validateFileBeforePreview(file, setDarkLogoError);
                if (isValid) {
                  field.onChange(file);
                } else {
                  field.onChange(null);
                }
              }}
            />
          )}
          name="darkLogoFile"
          control={control}
        />
      </Grid>
    </Grid>
  );
};

export default LogoUploader;
